import React, {Fragment, useEffect} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import clsx from 'clsx';
import config from '../../app/config';
import {marked} from 'marked';
import sanitizeHtml from 'sanitize-html';
import {hasPermission, Permissions, user} from '../../store/reducers/user';
import _ from 'lodash';
import AgyleButton from '../ui/buttons/agyleButton';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {clinicalNoteTypeDict} from '../../store/reducers/dicts';
import {formatNoteType} from './notesUtil';
import {ctxSplitScreen} from '../../store/reducers/patientContext';
import {Tooltip} from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
    singleNote: {
        '& .note-highlight': {
            display: 'inline',
            color: theme.palette.notes.textHighlight,
            backgroundColor: theme.palette.notes.backgroundHighlight,
        },
    },
    noteText: {
        padding: 8,
        paddingLeft: 22,
        color: theme.palette.notes.systemText,
        backgroundColor: theme.palette.notes.systemBackground,
        marginBottom: 10,
        borderRadius: '0px 0px 10px 10px',
        '&.notes': {
            backgroundColor: theme.palette.notes.background,
            color: theme.palette.notes.text,
        },
    },
    root: {
        color: theme.palette.notes.headerText,
        backgroundColor: theme.palette.notes.headerBackground,
        fontSize: '12pt',
        paddingLeft: 10,
        padding: 5,
        marginBottom: 1,
        borderRadius: '10px 10px 0px 0px',
        '&.hidden': {
            borderRadius: 10,
            marginBottom: 10,
        },
        '&.notes': {
            backgroundColor: theme.palette.notes.background,
            color: theme.palette.notes.text,
        },
        '&.location': {
            backgroundColor: theme.palette.notes.headerLocationBackground,
            color: theme.palette.notes.headerLocationText,
        },
        '&.task': {
            backgroundColor: theme.palette.notes.headerPlanBackground,
            color: theme.palette.notes.headerPlanText,
        },
        '&.patientMovement': {
            backgroundColor: theme.palette.notes.background,
            color: theme.palette.notes.text,
        },
    },
    actions: {
        marginLeft: 'auto',
        // marginRight: 5,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        '&.right': {
            justifyContent: 'flex-end',
        }
    },
    buttonGroup: {
        height: 24,
    },
    button: {
        height: 24,
        marginRight: 5,
    },
    buttonHidden: {
        padding: '4px 12px',
        background: '#E44',
        color: '#fff',
        borderRadius: 6,
        fontWeight: 'bold',
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        height: 30,
    },
    title: {},
    titleWho: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        fontWeight: 'bold',
        color: 'inherit',
        paddingTop: 5,
        paddingBottom: 5,
        gap: 6,
    },
    userSpecialty: {
        marginLeft: 10,
        padding: '2px 8px',
        backgroundColor: '#FFF',
        color: '#000',
        border: '2px solid #000',
        borderRadius: '5px',
        fontWeight: 'bold',
        fontSize: 12,
    },
    date: {
        // marginLeft: ,
        marginRight: 5,
        fontWeight: '500',
        display: 'inline-block',
        '&.modify': {
            marginLeft: 0,
        },
    },
    titleDate: {
        height: 30,
        fontSize: '80%',
        padding: '3px 12px',
        backgroundColor: '#FFFFFF',
        color: '#000',
        fontWeight: 'normal',
        borderRadius: 20,
        display: 'flex',
        alignItems: 'center',
        '&.specialty': {
            backgroundColor: '#222425',
            color: '#FFFFFF',
        },
        '&.reducedRadius': {
            borderRadius: 5,
        },
    },
    consultant: {
        display: 'inline-block',
        fontWeight: '500',
        marginRight: 10,
    },
    note: {
        overflowWrap: 'break-word',
        '& h1': {
            fontSize: 22,
            margin: '6px 0',
        },
        '& h2': {
            fontSize: 18,
            margin: '6px 0',
        },
        '& h3': {
            fontSize: 16,
            margin: '6px 0',
        },
        '& h4': {
            fontSize: 14,
            margin: '6px 0',
        },
        '& h5': {
            fontSize: 12,
            margin: '6px 0',
        },
        '& h6': {
            fontSize: 10,
            margin: '6px 0',
        },
    },
    lineThrough: {
        textDecoration: 'line-through',
    },
    noteFrom: {
        fontSize: 'small',
        fontStyle: 'italic',
    },
    smallFont: {
        fontSize: 12,
    },
}));

function SingleNoteComponent(props) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const userObject = useSelector(user);
    const {t} = useTranslation();
    let noteHtml = null;

    const splitScreen = useSelector(ctxSplitScreen);
    const isSSEnabled = splitScreen?.enabled;

    const isImage = _.isString(props?.note?.note) ? props?.note?.note?.startsWith('data:image/png;base64') : false;
    const clinicalNoteTypes = useSelector(clinicalNoteTypeDict);

    const isStrikethrough = () => props.note?.hidden || props.note?.canceled;
    const [textHidden, setTextHidden] = React.useState(isStrikethrough());

    useEffect(() => {
        setTextHidden(isStrikethrough());
    }, [props.note?.hidden, props.note?.canceled]);

    function escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\\\$&'); // $& means the whole matched string
    }

    const toggleTextHidden = () => {
        setTextHidden(!textHidden);
    };

    if (props.filterText && !isImage) {
        let regex = props.filterText;
        try {
            regex = new RegExp(escapeRegExp(props.filterText), 'ig');
        } catch (error) {
            console.error('Invalid regular expression');
        }
        noteHtml = props.note.note.replaceAll(
            regex,
            '<mark>$&</mark>',
        );
    }

    if (props?.note?.note) {
        if (isImage) {
            noteHtml = `<img style="max-width: 100%;" src="${props.note.note}" alt="body-map"/>`;
        } else {
            // noteHtml = marked(sanitizeHtml(noteHtml || props.note.note));
            noteHtml = marked(sanitizeHtml(noteHtml || props.note.note), {
                breaks: true,
            });
        }
    }

    const getFormTitle = () => {
        if (props.note.noteType) {
            return formatNoteType(props.note.noteType);
        } else {
            return isImage ? 'Body Map' : 'UNK';
        }
    };

    const getHeaderBackground = () => {
        if ((_.isNil(props?.note?.noteType) || !_.isNil(props.note?.recordType))) {
            return 'notes';
        } else if (!_.isNil(props?.note?.noteType)) {
            const task = ['studies.treatmentPlan', 'studies.medicalPlan', 'studies.nursingPlanNote'];
            if (props?.note?.noteType === 'Location') return 'location';
            else if (task.includes(props?.note?.noteType)) return 'task';
        }
    };

    const getCustomColor = () => {
        if (props.note?.recordType) {
            const find = _.find(clinicalNoteTypes, {__code: props.note?.recordType});
            return find?.color || '';
        }
    };

    const renderAction = () => {
        return (
            <div className={clsx(classes.actions, isSSEnabled && 'right')}>
                <div className={classes.consultant}>
                    {props.note.createdByName}
                </div>

                {(!isStrikethrough() && !props.disableSave) &&
                    <>
                        {props.onEdit && !isImage && !props.note?.noteType &&
                            <Tooltip title={t('Edit note')}>
                                <span>
                                    <IconButton
                                        disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_AND_SET_INCORRECT_CLINICAL_NOTES))}
                                        disableRipple
                                        onClick={() => {
                                            if (props.onEdit) {
                                                props.onEdit();
                                            }
                                        }}
                                        style={{color: '#000'}}
                                        size="small"
                                    >
                                        <EditOutlinedIcon fontSize="small"/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        }

                        {(!props.isLastOfType && props.note.noteType && props.handleError &&
                                (props.note?.createdBy === userObject?.userName || dispatch(hasPermission(Permissions.AGYLE_CAN_MENAGE_PREVIOUS_VALUES)))) &&
                            <AgyleButton
                                disabled={!(props.note?.createdBy === userObject?.userName || dispatch(hasPermission(Permissions.AGYLE_CAN_MENAGE_PREVIOUS_VALUES)))}
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    if (props.handleError) {
                                        props.handleError();
                                    }
                                }}
                                className={classes.button}
                            >
                                {t('Mark as Error')}
                            </AgyleButton>
                        }

                        {props.onHide && !props.note.noteType &&
                            <AgyleButton
                                disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_AND_SET_INCORRECT_CLINICAL_NOTES))}
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    if (props.onHide) {
                                        props.onHide();
                                    }
                                }}
                                className={classes.button}
                            >
                                {t('Mark as Incorrect')}
                            </AgyleButton>
                        }
                    </>
                }

                {isStrikethrough() &&
                    <AgyleButton
                        variant="contained"
                        size="small"
                        onClick={toggleTextHidden}
                        className={classes.button}
                    >
                        {!textHidden ? t('Hide Note') : t('Show Note')}
                    </AgyleButton>
                }

                {(props.onAudit && props.note.objectVersion > 0) &&
                    <AgyleButton
                        variant="contained"
                        size="small"
                        onClick={() => {
                            if (props.onAudit) {
                                props.onAudit();
                            }
                        }}
                        className={classes.button}
                    >
                        {t('Audit')}
                    </AgyleButton>
                }
            </div>
        )
    }

    const renderNoteHeader = () => {
        return (
            <div className={classes.singleNote}
                 style={{
                     marginBottom: textHidden && 20,
                 }}
            >
                {props.note &&
                    <div className={clsx(classes.root, textHidden && 'hidden', getHeaderBackground())}>
                        <div className={classes.title}>
                            {isSSEnabled &&
                                <div className={classes.smallFont}>
                                    {renderAction()}
                                </div>
                            }
                            <div className={clsx(classes.titleWho, isSSEnabled && classes.smallFont)}>
                                <div className={classes.date}>
                                    {moment(props.note.createdDate).format(config?.defaultDateFormat)} at {moment(props.note.createdDate).format(config?.defaultTimeFormat)}
                                </div>

                                {!_.isEqual(props.note.lastModifiedDate, props.note.createdDate) &&
                                    <div className={clsx(classes.date, 'modify')}>
                                        ({t('last modify:')} {moment(props.note.lastModifiedDate).format(config?.defaultDateFormat)} at {moment(props.note.lastModifiedDate).format(config?.defaultTimeFormat)})
                                    </div>
                                }

                                {(props.note.noteType || isImage) &&
                                    <div className={clsx(classes.titleDate, 'reducedRadius')}>
                                        {getFormTitle()}
                                    </div>
                                }

                                {props.note.recordType && (
                                    <span
                                        className={classes.titleDate}
                                        style={{backgroundColor: getCustomColor()}}
                                    >
                                        {_.startCase(props.note.recordType)}
                                    </span>
                                )}

                                {props.note.lastModifiedBySpecialty && (
                                    <span
                                        className={clsx(classes.titleDate, 'specialty')}
                                    >
                                          {props.note.lastModifiedBySpecialty}
                                      </span>
                                )}

                                {isStrikethrough() && (
                                    <span className={clsx(classes.buttonHidden, isSSEnabled && classes.smallFont)}>
                                        {t(props.note.canceled ? 'Marked as error' : 'Marked as incorrect')}
                                    </span>
                                )}
                                {!isSSEnabled && renderAction()}
                            </div>
                        </div>
                    </div>
                }
            </div>
        );
    };

    const renderComment = () => {
        const type = props.note?.comments[0];

        return <span className={classes.noteFrom}>{`Note from ${type?.comment || '-'}`}</span>;
    };

    const renderNoteBody = () => {
        return (
            !textHidden &&
            <div
                className={clsx(classes.noteText, (_.isNil(props?.note?.noteType) || !_.isNil(props.note?.recordType)) && 'notes')}
                style={{
                    marginBottom: !textHidden && 20,
                }}
            >
                <div className={classes.note}>
                    {!_.isEmpty(props.note?.comments) &&
                        renderComment()
                    }

                    <div
                        className={
                            isStrikethrough()
                                ? classes.lineThrough
                                : null
                        }
                        dangerouslySetInnerHTML={{__html: noteHtml}}
                    />
                </div>

                {props.note.cancelReason &&
                    <div className={classes.note}>
                        <h3>{t('Cancel Reason: ')}</h3>
                        <div
                            dangerouslySetInnerHTML={{__html: marked(sanitizeHtml(props.note.cancelReason))}}/>
                    </div>
                }
            </div>
        );
    };

    return (
        <>
            {renderNoteHeader()}
            {renderNoteBody()}
        </>
    );
}

export default SingleNoteComponent;
